import { Commerce } from '@interfaces/general';
import { MissingCommerceKeys, validateDeliveryCommerceValues } from '@pages/Commerce/hooks/commerce';
import { DELIVERY_CHANNELS } from '@pages/Commerce/validations/deliveryChannelRules';
import { validateCellPhone } from '@utils/validations';

export const validateCommerce = (commerce: Commerce) => {
  const missingKeys: MissingCommerceKeys[] = [];
  // step 1
  if (!commerce.country_code || commerce.country_code === 0) missingKeys.push('country_code');
  if (!commerce.telefono || !validateCellPhone(commerce.telefono.toString())) missingKeys.push('telefono');
  if (!commerce.prefijo_folio) missingKeys.push('prefijo_folio');
  if (!commerce.zona_horaria) missingKeys.push('zona_horaria');

  return missingKeys;
};

export const validateBusinessTypeValues = (commerce: Commerce) => {
  const missingValues: MissingCommerceKeys[] = [];
  // Business type step
  if (!commerce.business_type) missingValues.push('business_type');
  if (!commerce.business_sub_type) missingValues.push('business_sub_type');

  return missingValues;
};

/**
 * Descriptor de canal (canal ↔ objeto de métodos ↔ etiqueta). El onboarding NO mantiene su
 * propia lista: consume `DELIVERY_CHANNELS`, la fuente única compartida. Lo único propio de
 * este paso es el recorte a métodos UNIVERSALES de aquí abajo.
 */
export type OnboardingDeliveryChannel = (typeof DELIVERY_CHANNELS)[number];

/**
 * Métodos "universales" (los que no dependen de una integración de cobro externa).
 * Mismo recorte que el alta de comercios por partner en
 * `src/partner/ConfigureDeliverySection.tsx:10-12`: `enlace_pago` y
 * `payment_provider` requieren integraciones que un comercio recién creado no
 * tiene, así que no cuentan para cerrar el onboarding.
 */
export const UNIVERSAL_PAYMENT_METHODS = ['efectivo', 'tarjeta', 'transferencia'] as const;

export type UniversalPaymentMethod = (typeof UNIVERSAL_PAYMENT_METHODS)[number];

/**
 * Sub-conjunto de llaves que `validateDeliveryCommerceValues` emite para la
 * invariante "canal de entrega activo exige al menos un método de pago" (más el
 * caso "ningún canal activo"). Lista explícita a propósito: el resto de lo que
 * valida esa función (costo de envío, colonias, km, datos bancarios, propinas)
 * pertenece a "Tarifas y entregas", NO al onboarding — bloquear ahí por campos
 * que este paso ni siquiera muestra dejaría al comercio atorado sin salida.
 */
const CHANNEL_PAYMENT_KEYS: MissingCommerceKeys[] = [
  'activar_envio_domicilio',
  'activar_pasar_recoger',
  'activar_comer_restaurant',
  'metodos_pago_envio_domicilio',
  'metodos_pago_pasar_recoger',
  'metodos_pago_comer_restaurant',
];

/**
 * True si el canal tiene activo al menos un método universal.
 *
 * @param {Commerce} commerce Comercio completo.
 * @param {OnboardingDeliveryChannel} channel Descriptor del canal a evaluar.
 * @returns {boolean} True si hay efectivo, tarjeta o transferencia encendido.
 */
export const hasUniversalPaymentMethod = (commerce: Commerce, { methods }: OnboardingDeliveryChannel): boolean => {
  const paymentMethods = commerce[methods];
  return UNIVERSAL_PAYMENT_METHODS.some((method) => Boolean(paymentMethods?.[method]));
};

/**
 * Validación del paso "Métodos de entrega y pago" del onboarding.
 *
 * Se compone de dos capas:
 *  1. La invariante compartida del panel — se REUSA `validateDeliveryCommerceValues`
 *     (`@pages/Commerce/hooks/commerce`) filtrando su salida a las llaves de canal/método,
 *     para no escribir otra copia de la regla. `requireBankTransferDetails = false` a
 *     propósito: la CLABE se pide en "Tarifas y entregas", no aquí.
 *  2. Una restricción propia del onboarding: cada canal activo debe aceptar al menos un
 *     método UNIVERSAL. Cobrar sólo por proveedor de pagos (Clip/MercadoPago/Stripe) no
 *     alcanza para terminar el alta; fuera del onboarding el comercio sí puede
 *     configurar lo que quiera.
 *
 * @param {Commerce} commerce Comercio completo (el del wizard, con los cambios en curso).
 * @returns {MissingCommerceKeys[]} Llaves incompletas; vacío significa que se puede avanzar.
 */
export const validateDeliveryMethodsValues = (commerce: Commerce): MissingCommerceKeys[] => {
  const missingValues = validateDeliveryCommerceValues(commerce, false).filter((key) =>
    CHANNEL_PAYMENT_KEYS.includes(key),
  );

  DELIVERY_CHANNELS.forEach((deliveryChannel) => {
    const { channel, methods } = deliveryChannel;
    if (!commerce[channel]) return;
    if (hasUniversalPaymentMethod(commerce, deliveryChannel)) return;
    if (!missingValues.includes(methods)) missingValues.push(methods);
  });

  return missingValues;
};

export const validateAddressValues = (commerce: Commerce) => {
  const missingValues: MissingCommerceKeys[] = [];
  // step 2
  if (!commerce.address?.city) missingValues.push('address.city');
  if (!commerce.address?.state) missingValues.push('address.state');
  if (!commerce.address?.postalCode) missingValues.push('address.postalCode');
  if (!commerce.address?.neighborhood) missingValues.push('address.neighborhood');
  if (!commerce.address?.exteriorNumber) missingValues.push('address.exteriorNumber');
  if (!commerce.address?.street) missingValues.push('address.street');
  if (!commerce.address?.fullAddress) missingValues.push('address.fullAddress');
  if (!commerce.address?.latitude || commerce.address.latitude === 0) missingValues.push('address.latitude');
  if (!commerce.address?.longitude || commerce.address.longitude === 0) missingValues.push('address.longitude');

  return missingValues;
};
